import { Link, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { TwoFactorChallenge } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';

import { AuthPage } from '../components/auth-page';
import { DevGoogleAccountForm } from '../components/dev-google-account-form';
import { ServerError } from '../components/server-error';
import { TwoFactorStep } from '../components/two-factor-step';
import { useGoogleCallback } from '../hooks/use-google-callback';
import { googleFailure } from '../schemas/google-rules';
import { isTwoFactorChallenge, type ChallengeEnd } from '../schemas/two-factor-rules';

export interface GoogleCallbackPageProps {
  code: string | undefined;
  state: string | undefined;
  /** Google sends `error` (e.g. access_denied) instead of a code when the person backs out. */
  error: string | undefined;
  /** `dev=1`: the backend's development driver, which leaves picking the account to this page. */
  dev?: boolean | undefined;
}

/**
 * Where Google returns the browser. The code is single use, so it is exchanged exactly once per
 * page (the ref survives StrictMode's second effect run). A 202 continues with the same code step
 * as the password sign-in.
 */
export function GoogleCallbackPage({
  code: googleCode,
  state,
  error,
  dev,
}: GoogleCallbackPageProps) {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const exchange = useGoogleCallback();
  const { mutate } = exchange;
  const started = useRef<string | null>(null);
  const [challengeEnd, setChallengeEnd] = useState<ChallengeEnd | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);
  const code = googleCode ?? devCode ?? undefined;
  const result = exchange.data;
  const challenge: TwoFactorChallenge | null =
    result && isTwoFactorChallenge(result) && !challengeEnd ? result : null;
  const signedIn = result !== undefined && !isTwoFactorChallenge(result);

  const enter = useCallback(() => navigate({ to: '/org', replace: true }), [navigate]);
  const toSignIn = useCallback(() => navigate({ to: '/login' }), [navigate]);
  const onExpired = useCallback((reason: ChallengeEnd) => setChallengeEnd(reason), []);

  useEffect(() => {
    if (error || !code || !state || started.current === code) return;
    started.current = code;
    mutate({ code, state });
  }, [code, state, error, mutate]);

  useEffect(() => {
    if (signedIn) void enter();
  }, [signedIn, enter]);

  const tryAgain = (
    <Button asChild className="w-full">
      <Link to="/login">{t('google.tryAgain')}</Link>
    </Button>
  );
  const footer = <Link to="/login">{t('links.backToSignIn')}</Link>;

  if (dev && !code && state && !error) {
    return (
      <AuthPage title={t('google.dev.title')}>
        <DevGoogleAccountForm onPick={setDevCode} />
      </AuthPage>
    );
  }

  if (error || !code || !state) {
    return (
      <AuthPage title={t('google.cancelledTitle')} footer={footer}>
        <div className="flex flex-col gap-4">
          <p className="text-muted-foreground text-sm">
            {error ? t('google.cancelled') : t('google.missing')}
          </p>
          {tryAgain}
        </div>
      </AuthPage>
    );
  }

  if (challenge) {
    return (
      <AuthPage title={t('twoFactor.title')}>
        <TwoFactorStep
          challenge={challenge}
          onVerified={enter}
          onBack={() => void toSignIn()}
          onExpired={onExpired}
          backLabel={t('links.backToSignIn')}
        />
      </AuthPage>
    );
  }

  if (challengeEnd) {
    return (
      <AuthPage title={t(`twoFactor.${challengeEnd}Title`)} footer={footer}>
        <div className="flex flex-col gap-4">
          <Alert role="alert">
            <AlertDescription>{t('google.challengeEnded')}</AlertDescription>
          </Alert>
          {tryAgain}
        </div>
      </AuthPage>
    );
  }

  if (exchange.isError) {
    const failure = googleFailure(exchange.error);
    return (
      <AuthPage title={t(`google.failure.${failure}Title`)} footer={footer}>
        <div className="flex flex-col gap-4">
          {failure === 'failed' ? (
            <ServerError error={exchange.error} />
          ) : (
            <p role="alert" className="text-muted-foreground text-sm">
              {t(`google.failure.${failure}`)}
            </p>
          )}
          {tryAgain}
        </div>
      </AuthPage>
    );
  }

  return (
    <AuthPage title={t('google.signingInTitle')}>
      <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" aria-hidden />
        {t('google.signingIn')}
      </p>
    </AuthPage>
  );
}
